import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@/hooks/useWallet.ts'
import { useVaults } from '@/hooks/useVaults.ts'
import { useLocalCountdown } from '@/hooks/useLocalCountdown.ts'
import { useBeneficiary } from '@/hooks/useBeneficiary.ts'
import {
  useVaultMetadata,
  type VaultMetadata,
} from '@/hooks/useVaultMetadata.ts'
import { env } from '@/config/env.ts'
import { useActivityLog } from '@/hooks/useActivityLog.ts'
import { useEncryption } from '@/hooks/useEncryption.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import {
  sendCountdownStartedEmailsWebplatform,
  sendCountdownResetEmailsWebplatform,
  sendCountdownReminderToBenefactorWebplatform,
} from '@/services/notification/webplatformEmailSenders.ts'
import { useIPFS } from '@/hooks/useIPFS.ts'
import type { Vault } from '@/services/contract/contract.types.ts'
import {
  cn,
  safeDownloadFileName,
  triggerBrowserDownload,
  getFriendlyTransactionError,
} from '@/utils/index.ts'
import { assertBytesMatchEncryptionHash } from '@/utils/encryptionIntegrity.ts'

export function VaultDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const { address } = useWallet()
  const {
    fetchVaultDetails,
    getTimeRemaining,
    resetCountdown,
    startCountdown,
    updateBeneficiary,
    loading: vaultLoading,
  } = useVaults()
  const { canDecrypt, getDecryptionInfo } = useBeneficiary() //accessVault
  const { fetchByVaultId } = useVaultMetadata()
  const { logActivity } = useActivityLog()
  const enc = useEncryption()
  const ipfsHook = useIPFS()
  // Note: the docs/webplatform vault details page has a richer contact+notification UI.
  // For now we only keep the supported email/SMS resend logic elsewhere in the app.
  const { getProfileByWallet } = useProfile()

  const [vault, setVault] = useState<Vault | null>(null)
  const [meta, setMeta] = useState<VaultMetadata | null>(null)
  const [canDecryptVault, setCanDecryptVault] = useState<boolean | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionType, setActionType] = useState<
    'start' | 'reset' | 'decrypt' | null
  >(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)

  const [isUpdatingBeneficiary, setIsUpdatingBeneficiary] = useState(false)
  const [beneficiaryAddressInput, setBeneficiaryAddressInput] = useState('')
  const [beneficiaryUpdateLoading, setBeneficiaryUpdateLoading] =
    useState(false)
  const [beneficiaryUpdateError, setBeneficiaryUpdateError] = useState<
    string | null
  >(null)

  const [decryptedContent, setDecryptedContent] = useState<string | null>(null)
  const [fileDownloadedName, setFileDownloadedName] = useState<string | null>(
    null,
  )
  const [debugResponses, setDebugResponses] = useState<{
    contract: unknown
    metadata: unknown
  }>({ contract: null, metadata: null })

  // Decrypt source selection (beneficiary)
  type DecryptSource = 'ipfs' | 'file'
  const [decryptSource, setDecryptSource] = useState<DecryptSource>('ipfs')
  const [backupFile, setBackupFile] = useState<File | null>(null)
  const [backupFileError, setBackupFileError] = useState<string | null>(null)
  const backupFileRef = useRef<HTMLInputElement>(null)

  const countdownReminderEmailSent = useRef(false)

  let vaultId: bigint | null = null
  try {
    vaultId = id ? BigInt(id) : null
  } catch {
    vaultId = null
  }

  const { timeRemaining: localTimeRemaining } = useLocalCountdown(
    vault?.countdownStartTime,
    vault?.countdownDuration,
    vault?.isCountdownActive ?? false,
    vaultId != null ? () => getTimeRemaining(vaultId!) : undefined,
  )
  const timeRemaining = localTimeRemaining

  const isBenefactor =
    vault && address
      ? vault.benefactor.toLowerCase() === address.toLowerCase()
      : false

  const isBeneficiary =
    vault && address
      ? vault.beneficiary.toLowerCase() === address.toLowerCase()
      : false

  const loadVault = useCallback(async () => {
    console.log('[VaultDetailPage] loadVault called', {
      vaultId: vaultId?.toString(),
      address: address ?? 'none',
    })
    if (vaultId == null || !address) {
      console.log(
        '[VaultDetailPage] loadVault skipped (missing vaultId or address)',
      )
      return
    }
    let v: Awaited<ReturnType<typeof fetchVaultDetails>> = null
    let m: Awaited<ReturnType<typeof fetchByVaultId>> = null
    try {
      v = await fetchVaultDetails(vaultId)
      const vaultForLog = v
        ? {
            id: v.id.toString(),
            benefactor: v.benefactor,
            beneficiary: v.beneficiary,
            cid: v.cid,
            encryptionHash: v.encryptionHash,
            countdownDuration: v.countdownDuration.toString(),
            countdownStartTime: v.countdownStartTime.toString(),
            lastResetTime: v.lastResetTime.toString(),
            isCountdownActive: v.isCountdownActive,
            exists: v.exists,
          }
        : null
      console.log(
        '[VaultDetailPage] Contract getVaultDetails response:',
        vaultForLog,
      )
      setDebugResponses((prev) => ({ ...prev, contract: vaultForLog }))
      if (v) {
        setVault(v)
      }
    } catch (err) {
      console.error('[VaultDetailPage] Contract getVaultDetails error:', err)
      setDebugResponses((prev) => ({
        ...prev,
        contract: err instanceof Error ? err.message : String(err),
      }))
    }
    try {
      m = await fetchByVaultId(Number(vaultId))
      console.log(
        '[VaultDetailPage] Metadata (Supabase vault_metadata) response:',
        m,
      )
      setMeta(m)
      setDebugResponses((prev) => ({ ...prev, metadata: m }))
    } catch (err) {
      console.error('[VaultDetailPage] Metadata fetch error:', err)
      setDebugResponses((prev) => ({
        ...prev,
        metadata: err instanceof Error ? err.message : String(err),
      }))
    }
  }, [vaultId, address, fetchVaultDetails, fetchByVaultId])

  // Wait for wallet address so getVaultDetails is called with correct account (contract uses msg.sender)
  // Use vaultId != null so vault ID 0 is valid (0n is falsy so "address && vaultId" would skip it)
  useEffect(() => {
    if (address && vaultId != null) {
      loadVault()
    }
  }, [address, vaultId, loadVault])

  useEffect(() => {
    if (vaultId == null || !isBeneficiary) return
    canDecrypt(vaultId).then(setCanDecryptVault)
  }, [vaultId, isBeneficiary, canDecrypt])

  // `canDecrypt` depends on on-chain state which may flip only after the countdown expires.
  // When the countdown finishes, we re-check once so the decrypt button appears without refresh.
  const didRecheckDecryptOnExpireRef = useRef(false)
  useEffect(() => {
    didRecheckDecryptOnExpireRef.current = false
  }, [vaultId, isBeneficiary, vault?.isCountdownActive])

  useEffect(() => {
    if (
      vaultId == null ||
      !isBeneficiary ||
      timeRemaining == null ||
      timeRemaining > 0n
    ) {
      return
    }

    if (didRecheckDecryptOnExpireRef.current) return
    didRecheckDecryptOnExpireRef.current = true

    canDecrypt(vaultId).then(setCanDecryptVault)
  }, [vaultId, isBeneficiary, timeRemaining, canDecrypt])

  useEffect(() => {
    setDecryptedContent(null)
    setFileDownloadedName(null)
    setDecryptSource('ipfs')
    setBackupFile(null)
    setBackupFileError(null)
  }, [vaultId])

  useEffect(() => {
    if (!vault?.isCountdownActive) {
      countdownReminderEmailSent.current = false
    }
  }, [vault?.isCountdownActive])

  useEffect(() => {
    if (vaultId == null || !vault?.isCountdownActive || !vault) return

    const tick = async () => {
      try {
        const raw = await getTimeRemaining(vaultId)
        if (raw == null) return
        const remaining = raw
        const remNum = Number(remaining)
        const vid = Number(vaultId)

        if (
          remaining > 0n &&
          remNum <= 86400 &&
          !countdownReminderEmailSent.current
        ) {
          const bfProf = await getProfileByWallet(vault.benefactor)
          const benefactorEmail = bfProf?.email?.trim()
          if (!benefactorEmail) {
            countdownReminderEmailSent.current = true
            return
          }
          const res = await sendCountdownReminderToBenefactorWebplatform({
            vaultId: vid,
            benefactorEmail,
            benefactorName: bfProf?.name ?? undefined,
            timeRemainingSeconds: remNum,
            notifyEmail: bfProf?.notify_email,
          })
          if (res?.success) countdownReminderEmailSent.current = true
        }
      } catch {
        /* ignore */
      }
    }

    const id = window.setInterval(() => {
      void tick()
    }, 1000)
    return () => window.clearInterval(id)
  }, [
    vaultId,
    vault,
    vault?.isCountdownActive,
    getTimeRemaining,
    meta,
    fetchByVaultId,
    getProfileByWallet,
  ])

  const handleStartCountdown = async () => {
    if (vaultId == null) return
    setActionLoading(true)
    setActionType('start')
    setActionError(null)
    setActionSuccess(null)
    try {
      const result = await startCountdown(vaultId)
      if (!result) {
        setActionError(
          t('common.transactionFailed', {
            defaultValue: 'Transaction failed or was rejected.',
          }),
        )
        return
      }

      await logActivity('countdown_started', Number(vaultId))
      const v = await fetchVaultDetails(vaultId)
      const m = await fetchByVaultId(Number(vaultId))
      if (v) {
        const bfProf = await getProfileByWallet(v.benefactor)
        const benProf = await getProfileByWallet(v.beneficiary)
        await sendCountdownStartedEmailsWebplatform({
          vaultId: Number(vaultId),
          countdownDurationSeconds: Number(v.countdownDuration),
          benefactorAddress: v.benefactor,
          beneficiaryAddress: v.beneficiary,
          benefactorEmail: bfProf?.email ?? null,
          beneficiaryEmail: m?.beneficiary_email ?? null,
          benefactorName: bfProf?.name ?? null,
          notifyBenefactorEmail: bfProf?.notify_email,
          notifyBeneficiaryEmail: benProf?.notify_email,
        })
      }
      await loadVault()
      setActionSuccess(t('activity.action_countdown_started'))
    } catch (e) {
      setActionError(getFriendlyTransactionError(e))
    } finally {
      setActionLoading(false)
      setActionType(null)
    }
  }

  const handleResetCountdown = async () => {
    if (vaultId == null) return
    setActionLoading(true)
    setActionType('reset')
    setActionError(null)
    try {
      const result = await resetCountdown(vaultId)
      if (result) {
        await logActivity('countdown_reset', Number(vaultId))
        const v = await fetchVaultDetails(vaultId)
        const m = await fetchByVaultId(Number(vaultId))
        if (v) {
          const bfProf = await getProfileByWallet(v.benefactor)
          const benProf = await getProfileByWallet(v.beneficiary)
          await sendCountdownResetEmailsWebplatform({
            vaultId: Number(vaultId),
            benefactorEmail: bfProf?.email ?? null,
            beneficiaryEmail: m?.beneficiary_email ?? null,
            benefactorName: bfProf?.name ?? null,
            notifyBenefactorEmail: bfProf?.notify_email,
            notifyBeneficiaryEmail: benProf?.notify_email,
          })
        }
        await loadVault()
      }
    } catch (e) {
      setActionError(getFriendlyTransactionError(e))
    } finally {
      setActionLoading(false)
      setActionType(null)
    }
  }

  const handleAccessVault = async () => {
    if (vaultId == null || !address) return
    setActionLoading(true)
    setActionType('decrypt')
    setActionError(null)
    setDecryptedContent(null)
    setFileDownloadedName(null)
    setBackupFileError(null)
    try {
      let ciphertext: string
      let hashForDecrypt: string
      let payloadVaultType: 'text' | 'file' | undefined
      let payloadOriginalFilename: string | undefined

      if (decryptSource === 'file') {
        // ── Path A: user-supplied backup .json ──────────────────────────────
        if (!backupFile) {
          setBackupFileError(t('vaultDetail.backupFileRequired'))
          setActionLoading(false)
          setActionType(null)
          return
        }
        let parsed: {
          version?: number
          vaultType?: 'text' | 'file'
          originalFilename?: string
          ciphertext?: string
          dataToEncryptHash?: string
          chain?: string
        }
        try {
          parsed = JSON.parse(await backupFile.text())
        } catch {
          throw new Error(t('vaultDetail.backupFileInvalid'))
        }
        if (!parsed.ciphertext || !parsed.dataToEncryptHash) {
          throw new Error(t('vaultDetail.backupFileInvalid'))
        }
        ciphertext = parsed.ciphertext
        hashForDecrypt = parsed.dataToEncryptHash
        payloadVaultType = parsed.vaultType
        payloadOriginalFilename = parsed.originalFilename
      } else {
        // ── Path B: fetch from IPFS (original behaviour) ────────────────────
        let info: { cid: string; encryptionHash: `0x${string}` } | null = null
        if (vault?.cid && vault?.encryptionHash) {
          info = { cid: vault.cid, encryptionHash: vault.encryptionHash }
        }
        if (!info) {
          info = await getDecryptionInfo(vaultId)
        }
        if (!info) {
          throw new Error(
            'Could not get decryption info from contract. Check that the vault exists and you are on the correct network.',
          )
        }
        if (!info.cid) throw new Error('No CID on vault')

        try {
          const payload = await ipfsHook.downloadJSON<{
            version?: number
            vaultType?: 'text' | 'file'
            originalFilename?: string
            ciphertext: string
            dataToEncryptHash?: string
            chain?: string
          }>(info.cid)
          if (payload?.ciphertext) {
            ciphertext = payload.ciphertext
            hashForDecrypt = payload.dataToEncryptHash ?? info.encryptionHash
            payloadVaultType = payload.vaultType
            payloadOriginalFilename = payload.originalFilename
          } else {
            const raw = await ipfsHook.downloadText(info.cid)
            if (!raw) throw new Error('Could not load vault data from Pinata')
            ciphertext = raw
            hashForDecrypt = info.encryptionHash
          }
        } catch (e) {
          const raw = await ipfsHook.downloadText(
            (vault?.cid && vault.cid) || '',
          )
          if (!raw)
            throw new Error(
              'Could not load vault data from Pinata: ' +
                (e instanceof Error ? e.message : String(e)),
            )
          ciphertext = raw
          hashForDecrypt = vault!.encryptionHash
        }
      }

      // Resolve info.encryptionHash for integrity check (needed in both paths)
      const encryptionHashForCheck: `0x${string}` =
        decryptSource === 'file'
          ? (vault?.encryptionHash ?? ('0x' as `0x${string}`))
          : (vault?.encryptionHash ?? ('0x' as `0x${string}`))

      let effectiveMeta = meta
      if (effectiveMeta == null && vaultId != null) {
        effectiveMeta = await fetchByVaultId(Number(vaultId))
        if (effectiveMeta) setMeta(effectiveMeta)
      }

      // 3. Lit Chipotle: connect and authenticate (wallet signature proves ownership)
      if (!enc.isConnected) await enc.connect()
      const authResult = await enc.authenticate()
      if (!authResult)
        throw new Error(
          'Authentication failed. Check that your wallet is connected.',
        )

      // BL-X-01: chain pinning is now done inside the proxy from the server
      // CONTRACT_NETWORK env var. The IPFS payload's `chain` field is kept
      // only as descriptive metadata (it isn't read here any more).

      const isFileVault =
        payloadVaultType === 'file' ||
        (payloadVaultType == null && effectiveMeta?.vault_type === 'file')

      if (isFileVault) {
        const blob = await enc.decryptToFile(
          ciphertext,
          hashForDecrypt,
          Number(vaultId),
          address,
        )
        if (blob == null) {
          const reason =
            enc.error ||
            'File decryption failed. Ensure you are the beneficiary, the countdown has expired, and the vault was encrypted with this chain.'
          throw new Error(reason)
        }
        const fileBytes = new Uint8Array(await blob.arrayBuffer())
        await assertBytesMatchEncryptionHash(fileBytes, encryptionHashForCheck)
        const verifiedBlob = new Blob([fileBytes.buffer as ArrayBuffer])
        const rawName =
          payloadOriginalFilename?.trim() ||
          effectiveMeta?.original_filename?.trim() ||
          `vault-${vaultId}-decrypted.bin`
        triggerBrowserDownload(verifiedBlob, rawName)
        setFileDownloadedName(safeDownloadFileName(rawName))
      } else {
        const decrypted = await enc.decryptToString(
          ciphertext,
          hashForDecrypt,
          Number(vaultId),
          address,
        )
        if (decrypted == null) {
          const reason =
            enc.error ||
            'Decryption failed. Ensure you are the beneficiary, the countdown has expired, and the vault was encrypted with this chain.'
          throw new Error(reason)
        }
        await assertBytesMatchEncryptionHash(
          new TextEncoder().encode(decrypted),
          encryptionHashForCheck,
        )
        setDecryptedContent(
          decrypted.trim() === ''
            ? `(${t('vaultDetail.emptyContent')})`
            : decrypted,
        )
      }

      // 4. Record access on contract (after successful decrypt / download even if this fails)
      try {
        // await accessVault(vaultId)
        await logActivity('vault_accessed', Number(vaultId))
      } catch {
        // Non-blocking: content already shown or file already downloaded
      }
    } catch (e) {
      setActionError(getFriendlyTransactionError(e))
    } finally {
      setActionLoading(false)
      setActionType(null)
    }
  }

  const openUpdateBeneficiary = () => {
    if (!vault) return
    setBeneficiaryUpdateError(null)
    setBeneficiaryAddressInput(vault.beneficiary)
    setIsUpdatingBeneficiary(true)
  }

  const closeUpdateBeneficiary = () => {
    setIsUpdatingBeneficiary(false)
    setBeneficiaryUpdateError(null)
  }

  const handleSaveUpdateBeneficiary = async () => {
    if (vaultId == null) return
    const newAddr = beneficiaryAddressInput.trim()
    if (!newAddr.startsWith('0x') || newAddr.length !== 42) {
      setBeneficiaryUpdateError('Invalid wallet address.')
      return
    }

    setBeneficiaryUpdateLoading(true)
    setBeneficiaryUpdateError(null)
    try {
      const result = await updateBeneficiary(vaultId, newAddr as `0x${string}`)
      if (result) {
        await logActivity('beneficiary_updated', Number(vaultId), {
          newBeneficiary: newAddr,
        })
        await loadVault()
        closeUpdateBeneficiary()
      }
    } catch (e) {
      setBeneficiaryUpdateError(getFriendlyTransactionError(e))
    } finally {
      setBeneficiaryUpdateLoading(false)
    }
  }

  const formatTimeRemaining = (seconds: bigint): string => {
    const s = Number(seconds)
    if (s <= 0) return t('vaultDetail.expired')
    const days = Math.floor(s / 86400)
    const hours = Math.floor((s % 86400) / 3600)
    const mins = Math.floor((s % 3600) / 60)
    const secs = Math.floor(s % 60)

    if (days > 0) return `${days}d ${hours}h ${mins}m`
    if (hours > 0) return `${hours}h ${mins}m`
    if (mins > 0) return `${mins}m ${secs}s`
    return `${secs}s`
  }

  const beneficiaryEmail = meta?.beneficiary_email?.trim() ?? ''
  const beneficiaryPrimary = beneficiaryEmail || vault?.beneficiary || ''

  const debugBlock = env.enableDebug ? (
    <div className="mt-8 rounded-lg border border-[#333] bg-[#1a1a1a] p-4 text-[0.8125rem] text-[#ccc]">
      <strong>
        Debug (vaultId={vaultId?.toString() ?? 'null'}, address=
        {address ? `${address.slice(0, 8)}...` : 'null'})
      </strong>
      <pre className="mt-2 max-h-[180px] overflow-auto font-mono break-words whitespace-pre-wrap">
        {JSON.stringify(debugResponses, null, 2)}
      </pre>
    </div>
  ) : null

  const pageShell = 'mx-auto max-w-content px-4 py-8 md:px-8 md:py-12'

  if (vaultId == null) {
    return (
      <div className={pageShell}>
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center">
          {t('vaultDetail.vaultNotFound')}
        </div>
        {debugBlock}
      </div>
    )
  }

  // Wait for wallet before trying to load (getVaultDetails needs account for contract msg.sender)
  if (!address) {
    return (
      <div className={pageShell}>
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center">
          {t('vaultDetail.loadingVault')}
        </div>
        {debugBlock}
      </div>
    )
  }

  if (vaultLoading && !vault) {
    return (
      <div className={pageShell}>
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center">
          {t('vaultDetail.loadingVault')}
        </div>
        {debugBlock}
      </div>
    )
  }

  if (!vault) {
    return (
      <div className={pageShell}>
        <div className="text-fg-muted flex min-h-[300px] items-center justify-center">
          {t('vaultDetail.vaultNotFound')}
        </div>
        {debugBlock}
      </div>
    )
  }

  return (
    <div className={pageShell}>
      <Link
        to="/dashboard"
        className="text-fg-secondary duration-fast hover:text-accent mb-6 inline-flex items-center gap-2 text-sm no-underline transition-colors"
      >
        {t('vaultDetail.backToDashboard')}
      </Link>

      <div className="mb-12 flex flex-wrap items-start justify-between gap-4">
        <h1 className="text-fg text-[1.75rem] font-bold max-md:text-[1.375rem]">
          {t('vaultDetail.vaultTitle', { id: vault.id.toString() })}
          {meta?.vault_name && ` — ${meta.vault_name}`}
        </h1>
        <span
          className={cn(
            'rounded-full px-3 py-1 text-xs font-semibold tracking-wide uppercase',
            isBenefactor
              ? 'bg-accent-muted text-accent'
              : 'bg-teal-muted text-teal',
          )}
        >
          {isBenefactor
            ? t('vaultDetail.benefactor')
            : t('vaultDetail.beneficiary')}
        </span>
      </div>

      {actionError && (
        <div className="border-error-border bg-error-muted text-error mb-6 rounded-md border p-4 text-sm">
          {actionError}
        </div>
      )}
      {actionSuccess && (
        <div className="border-success/40 bg-success/10 text-success mb-6 rounded-md border p-4 text-sm font-medium">
          {actionSuccess}
        </div>
      )}

      <div className="border-line bg-surface-card mb-12 rounded-lg border p-6">
        <h2 className="text-fg mb-4 text-[1.25rem] font-bold">
          Countdown Status
        </h2>

        <div className="flex items-center gap-4">
          <span
            className={cn(
              'h-3 w-3 shrink-0 rounded-full',
              vault.isCountdownActive
                ? timeRemaining !== null && timeRemaining <= 0n
                  ? 'bg-success'
                  : 'bg-warning'
                : 'bg-fg-muted',
            )}
          />

          <div className="min-w-0 flex-1">
            <div className="text-fg text-[0.9375rem] font-semibold">
              {!vault.isCountdownActive
                ? t('vaultDetail.countdownNotStarted')
                : timeRemaining !== null && timeRemaining <= 0n
                  ? t('vaultDetail.countdownExpired')
                  : t('vaultDetail.countdownActive')}
            </div>

            {vault.isCountdownActive &&
              timeRemaining !== null &&
              timeRemaining > 0n && (
                <div className="text-fg-secondary mt-1 text-[0.8125rem]">
                  Time remaining: {formatTimeRemaining(timeRemaining)}
                </div>
              )}
          </div>
        </div>

        {vault.isCountdownActive && (
          <div className="mt-5 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-fg-muted text-xs font-medium tracking-wide uppercase">
                Duration
              </span>
              <span className="text-fg text-[0.9375rem] font-medium">
                {Math.max(0, Math.round(Number(vault.countdownDuration) / 60))}{' '}
                minutes
              </span>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-fg-muted text-xs font-medium tracking-wide uppercase">
                Start Time
              </span>
              <span className="text-fg text-[0.9375rem] font-medium">
                {new Date(
                  Number(vault.countdownStartTime) * 1000,
                ).toLocaleString()}
              </span>
            </div>

            {timeRemaining !== null && timeRemaining > 0n && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-fg-muted text-xs font-medium tracking-wide uppercase">
                  Time Remaining
                </span>
                <span className="text-warning text-[0.9375rem] font-semibold">
                  {formatTimeRemaining(timeRemaining)}
                </span>
              </div>
            )}

            {timeRemaining !== null && timeRemaining <= 0n && (
              <div className="border-success/40 bg-success/10 rounded-lg border p-4">
                <p className="text-success font-semibold">
                  {isBenefactor
                    ? 'the beneficiary can now decrypt the vault'
                    : t('vaultDetail.countdownExpired')}
                </p>
              </div>
            )}

            {vault.lastResetTime > 0n && (
              <div className="flex items-center justify-between gap-4">
                <span className="text-fg-muted text-xs font-medium tracking-wide uppercase">
                  Last Reset
                </span>
                <span className="text-fg text-[0.9375rem] font-medium">
                  {new Date(
                    Number(vault.lastResetTime) * 1000,
                  ).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mb-12">
        <h2 className="text-fg mb-6 text-[1.25rem] font-bold">
          Vault Information
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="border-line bg-surface-card rounded-lg border p-6">
            <div className="text-fg-muted mb-1 text-xs font-medium tracking-wide uppercase">
              {t('vaultDetail.benefactor')}
            </div>
            <div className="text-fg font-mono text-[0.8125rem] break-all">
              {vault.benefactor}
            </div>
          </div>
          <div className="border-line bg-surface-card rounded-lg border p-6">
            <div className="text-fg-muted mb-1 text-xs font-medium tracking-wide uppercase">
              {t('vaultDetail.beneficiary')}
            </div>
            <div className="text-fg font-mono text-[0.8125rem] break-all">
              {beneficiaryPrimary}
            </div>
            {beneficiaryEmail && (
              <div className="text-fg-secondary mt-2 font-mono text-[0.75rem] break-all opacity-70">
                {vault.beneficiary}
              </div>
            )}
            {beneficiaryEmail && (
              <div className="text-fg-muted mt-1 text-[0.75rem]">
                {t('vaultDetail.walletAddress', {
                  defaultValue: 'Wallet Address',
                })}
              </div>
            )}
          </div>

          {vault.cid && (
            <div className="border-line bg-surface-card rounded-lg border p-6">
              <div className="text-fg-muted mb-1 text-xs font-medium tracking-wide uppercase">
                IPFS CID
              </div>
              <div className="text-fg font-mono text-[0.8125rem] break-all">
                {vault.cid}
              </div>
            </div>
          )}

          <div className="border-line bg-surface-card rounded-lg border p-6">
            <div className="text-fg-muted mb-1 text-xs font-medium tracking-wide uppercase">
              Encryption Hash
            </div>
            <div className="text-fg font-mono text-[0.8125rem] break-all">
              {vault.encryptionHash}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4">
        {isBenefactor &&
          vault.isCountdownActive &&
          timeRemaining !== null &&
          timeRemaining > 0n && (
            <button
              type="button"
              className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-md border px-6 py-3 text-[0.9375rem] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleResetCountdown}
              disabled={actionLoading}
            >
              {t('vaultDetail.resetCountdown')}
            </button>
          )}

        {isBenefactor && !isUpdatingBeneficiary && !vault.isCountdownActive && (
          <button
            type="button"
            className="border-line bg-surface-muted text-fg duration-fast hover:border-accent cursor-pointer rounded-md border px-6 py-3 text-[0.9375rem] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
            onClick={openUpdateBeneficiary}
            disabled={actionLoading}
          >
            {t('vaultDetail.updateBeneficiary')}
          </button>
        )}

        {isBenefactor && isUpdatingBeneficiary && (
          <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center">
            <input
              type="text"
              value={beneficiaryAddressInput}
              onChange={(e) => setBeneficiaryAddressInput(e.target.value)}
              placeholder={t('vaultDetail.updateBeneficiaryPrompt')}
              aria-label="Beneficiary wallet address"
              className="border-line bg-surface-muted text-fg focus:border-accent w-full rounded-md px-4 py-2 text-[0.9375rem] outline-none md:w-[360px]"
              disabled={beneficiaryUpdateLoading}
            />
            <div className="flex gap-3">
              <button
                type="button"
                className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleSaveUpdateBeneficiary}
                disabled={beneficiaryUpdateLoading}
              >
                {beneficiaryUpdateLoading
                  ? 'Saving...'
                  : t('vaultDetail.updateBeneficiary')}
              </button>
              <button
                type="button"
                className="border-line text-fg-secondary duration-fast hover:border-accent cursor-pointer rounded-md border bg-transparent px-6 py-3 text-[0.9375rem] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50"
                onClick={closeUpdateBeneficiary}
                disabled={beneficiaryUpdateLoading}
              >
                {t('common.cancel')}
              </button>
            </div>
            {beneficiaryUpdateError && (
              <div className="border-error-border bg-error-muted text-error w-full rounded-md border p-3 text-sm md:w-auto">
                {beneficiaryUpdateError}
              </div>
            )}
          </div>
        )}

        {isBeneficiary && !vault.isCountdownActive && (
          <button
            type="button"
            className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleStartCountdown}
            disabled={actionLoading}
          >
            {actionLoading && actionType === 'start'
              ? 'Starting countdown...'
              : t('vaultDetail.startCountdown')}
          </button>
        )}

        {isBeneficiary && canDecryptVault && (
          <div className="border-line bg-surface-card mb-6 w-full rounded-lg border p-6">
            <h3 className="text-fg mb-4 text-[1rem] font-semibold">
              {t('vaultDetail.decryptSource')}
            </h3>

            {/* Source toggle */}
            <div className="mb-4 flex gap-2">
              <button
                type="button"
                id="decrypt-source-ipfs"
                className={cn(
                  'duration-fast cursor-pointer rounded-md border px-4 py-2 text-sm font-medium transition-all',
                  decryptSource === 'ipfs'
                    ? 'border-accent bg-accent-muted text-accent'
                    : 'border-line bg-surface-muted text-fg-secondary',
                )}
                onClick={() => {
                  setDecryptSource('ipfs')
                  setBackupFile(null)
                  setBackupFileError(null)
                  if (backupFileRef.current) backupFileRef.current.value = ''
                }}
                disabled={actionLoading}
              >
                {t('vaultDetail.decryptSourceIPFS')}
              </button>
              <button
                type="button"
                id="decrypt-source-file"
                className={cn(
                  'duration-fast cursor-pointer rounded-md border px-4 py-2 text-sm font-medium transition-all',
                  decryptSource === 'file'
                    ? 'border-accent bg-accent-muted text-accent'
                    : 'border-line bg-surface-muted text-fg-secondary',
                )}
                onClick={() => setDecryptSource('file')}
                disabled={actionLoading}
              >
                {t('vaultDetail.decryptSourceFile')}
              </button>
            </div>

            {/* File upload area — only shown when 'file' source is selected */}
            {decryptSource === 'file' && (
              <div className="mb-4">
                <div
                  className={cn(
                    'duration-fast flex min-h-[80px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-[border-color]',
                    backupFileError
                      ? 'border-error hover:border-error'
                      : 'border-line hover:border-accent',
                  )}
                  onClick={() => backupFileRef.current?.click()}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && backupFileRef.current?.click()
                  }
                  role="button"
                  tabIndex={0}
                >
                  {backupFile ? (
                    <p className="text-accent text-center text-sm break-all">
                      {backupFile.name}
                    </p>
                  ) : (
                    <p className="text-fg-muted text-center text-sm">
                      {t('vaultDetail.backupFilePlaceholder')}
                    </p>
                  )}
                </div>
                {backupFileError && (
                  <p className="text-error mt-1 text-xs" role="alert">
                    {backupFileError}
                  </p>
                )}
                <input
                  ref={backupFileRef}
                  id="backup-file-input"
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null
                    setBackupFile(f)
                    setBackupFileError(null)
                  }}
                />
              </div>
            )}

            <button
              type="button"
              id="decrypt-vault-btn"
              className="bg-gradient-brand text-fg-inverse duration-fast cursor-pointer rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleAccessVault}
              disabled={
                actionLoading || (decryptSource === 'file' && !backupFile)
              }
            >
              {actionLoading && actionType === 'decrypt'
                ? 'Decrypting...'
                : t('vaultDetail.decryptWithSource')}
            </button>
          </div>
        )}
      </div>

      {isBeneficiary &&
        vault.isCountdownActive &&
        timeRemaining !== null &&
        timeRemaining > 0n && (
          <div className="border-warning/40 bg-warning/10 mb-6 w-full rounded-lg border p-4">
            <p className="text-warning font-semibold">
              Countdown is still running ({formatTimeRemaining(timeRemaining)}{' '}
              remaining). You can decrypt when it expires.
            </p>
          </div>
        )}

      {isBeneficiary &&
        vault.isCountdownActive &&
        timeRemaining !== null &&
        timeRemaining <= 0n &&
        canDecryptVault === false && (
          <div className="border-error-border/40 bg-error-muted/30 mb-6 w-full rounded-lg border p-4">
            <p className="text-error font-semibold">
              Countdown has expired, but you do not have permission to decrypt
              this vault yet. Please contact support.
            </p>
          </div>
        )}

      {!isBenefactor && !isBeneficiary && (
        <p className="text-fg-secondary mb-6 text-[0.8125rem]">
          You are not the benefactor or beneficiary of this vault.
        </p>
      )}

      {fileDownloadedName && (
        <div className="mt-12">
          <h3 className="text-fg mb-4 text-[1.25rem] font-bold">
            {t('vaultDetail.fileDownloadedTitle')}
          </h3>
          <div className="border-line bg-surface-card rounded-lg border p-6">
            <p className="text-fg m-0 text-[0.9375rem]">
              {t('vaultDetail.fileDownloaded', { name: fileDownloadedName })}
            </p>
          </div>
        </div>
      )}

      {decryptedContent && (
        <div className="mt-12">
          <h3 className="text-fg mb-4 text-[1.25rem] font-bold">
            {t('vaultDetail.decryptedContent')}
          </h3>
          <div className="border-line bg-surface-card rounded-lg border p-6">
            <pre className="text-fg font-mono text-sm break-words whitespace-pre-wrap">
              {decryptedContent}
            </pre>
          </div>
        </div>
      )}

      {env.enableDebug && (
        <div className="border-line bg-surface-raised mt-12 rounded-md border p-6 text-[0.8125rem]">
          <h3 className="mb-2">Debug: Contract &amp; metadata responses</h3>
          <div className="mb-4">
            <strong>Contract (getVaultDetails):</strong>
            <pre className="mt-1 max-h-[200px] overflow-auto font-mono break-words whitespace-pre-wrap">
              {vault
                ? JSON.stringify(
                    {
                      id: vault.id.toString(),
                      benefactor: vault.benefactor,
                      beneficiary: vault.beneficiary,
                      cid: vault.cid,
                      encryptionHash: vault.encryptionHash,
                      countdownDuration: vault.countdownDuration.toString(),
                      countdownStartTime: vault.countdownStartTime.toString(),
                      lastResetTime: vault.lastResetTime.toString(),
                      isCountdownActive: vault.isCountdownActive,
                      exists: vault.exists,
                    },
                    null,
                    2,
                  )
                : 'null'}
            </pre>
          </div>
          <div>
            <strong>Metadata (Supabase vault_metadata):</strong>
            <pre className="mt-1 max-h-[200px] overflow-auto font-mono break-words whitespace-pre-wrap">
              {meta !== null && meta !== undefined
                ? JSON.stringify(meta, null, 2)
                : 'null'}
            </pre>
          </div>
        </div>
      )}
      {debugBlock}
    </div>
  )
}
